<template>
  <form v-if="isLoaded"
        @submit.prevent="submitForm"
        novalidate client-dropdown="" sub-client-id="" submit-form>
    <div class="row">
      <div class="col-12">
        <BaseInput v-model="formData.name"
                   :required="true"
                   label="Name"
                   :validation="v$.name"
                   :messages="v$.name.required.$message"/>
      </div>
      <div class="col-12">
        <SelectVue
            v-model="formData.parentId"
            :options="clientDropdown"
            :is-clear="true"
            :placeholder="$t('placeholder.client_name')"
            :validation="v$.parentId"
            :messages="v$.parentId.required.$message"/>
      </div>
    </div>
    <div class="d-flex justify-content-center w-100 mt-3">
      <SaveButton
          :label="$t('general.button.save')"
          :loading="isSubmitting"/>
    </div>
  </form>
</template>

<script setup lang="ts">
import clientService from '@/api/Client';
import BaseInput from '@/components/base/BaseInput.vue';
import SelectVue from '@/components/SelectVue.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import {requiredRule} from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import {computed, defineEmits, defineProps, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {IFormRules, ISubClientForm} from "@/interfaces/Client/ISubClient";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const isLoaded = ref(false);
const isSubmitting = ref(false);
const emit = defineEmits(['update:modelValue', 'update:submitForm']);

const props = defineProps({
  subClientId: {
    type: [String, Number, null],
    required: true,
  },
  clientDropdown: {
    type: Array,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref<ISubClientForm>({
  name: '',
  parentId: '',
});

const formRules = ref<IFormRules>({
  name: requiredRule(t),
  parentId: requiredRule(t),
})

const computedRules = computed(() => formRules.value);
const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {

    props.subClientId ?
        await clientService.putSubClient(formData.value, +props.subClientId) :
        await clientService.postSubClient(formData.value)

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);

    SuccessToast(t('general.toasts.save_success'));

  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getSubClientData(id: number | string) {
  try {
    const {data} = await clientService.getSubClient(+id);
    const parentClientId = data.data.parent.id;

    formData.value = {
      ...data.data,
      parentId: parentClientId,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (props.subClientId) {
    await getSubClientData(props.subClientId);
  }

  isLoaded.value = true;
});
</script>
<style scoped>

</style>